import { Exclude, Expose, plainToClass, Type } from "class-transformer";
import * as t from "io-ts";
import { InvoiceGroupingDefaultArrayValidator, InvoiceGroupingDefaultEntity } from "./invoice-grouping-default";
import { Default } from "../util/decorators";
import {ObjectPreferenceArrayCodec, ObjectPreferenceEntity} from "./object-preference";
import {
  WaterGoPreferenceEntity,
  WaterGoPreferenceCodec
} from "./water-go-preference";
import {OilGoPreferenceEntity, OilGoPreferenceCodec} from "./oil-go-preference";

/** Validator for a company region profile. */
export const CompanyRegionProfileValidator = t.intersection([
  t.type({
    companyId: t.string,
    regionId: t.string,
    active: t.boolean,
  }),
  t.partial({
    formalName: t.string,
    address1: t.string,
    address2: t.string,
    city: t.string,
    state: t.string,
    zipCode: t.string,
    billingAddress1: t.string,
    billingAddress2: t.string,
    billingCity: t.string,
    billingState: t.string,
    billingZipCode: t.string,
    phone: t.string,
    emailAddress: t.string,
    dispatchRequestEnabled: t.boolean,
    dispatchRequestAcceptTimeLimit: t.number,
    invoiceGroupingDefaultList: InvoiceGroupingDefaultArrayValidator,
    objectPreferences: ObjectPreferenceArrayCodec,
    waterGoPreferences: WaterGoPreferenceCodec,
    oilGoPreference: OilGoPreferenceCodec,
  }),
]);

/** Validator for an array of company region profiles. */
export const CompanyRegionProfileArrayValidator = t.array(
  CompanyRegionProfileValidator
);

export type CompanyRegionProfile = t.TypeOf<
  typeof CompanyRegionProfileValidator
>;
export type CompanyRegionProfileArray = t.TypeOf<
  typeof CompanyRegionProfileArrayValidator
>;

/**
 * A company region profile.
 */
@Exclude()
export class CompanyRegionProfileEntity {
  @Expose() companyId!: string;
  @Expose() regionId!: string;
  @Expose() formalName?: string;
  @Expose() address1?: string;
  @Expose() address2?: string;
  @Expose() city?: string;
  @Expose() state?: string;
  @Expose() zipCode?: string;
  @Expose() billingAddress1?: string;
  @Expose() billingAddress2?: string;
  @Expose() billingCity?: string;
  @Expose() billingState?: string;
  @Expose() billingZipCode?: string;
  @Expose() phone?: string;
  @Expose() emailAddress?: string;
  @Expose({ name: 'dispatchRequestEnabled' }) isDispatchRequestEnabled = true;
  @Expose() dispatchRequestAcceptTimeLimit?: number;
  @Expose({ name: 'active' }) isActive = true;

  @Expose({ name: 'invoiceGroupingDefaultList' })
  @Type(() => InvoiceGroupingDefaultEntity)
  @Default([])
  invoiceGroupingDefaults: InvoiceGroupingDefaultEntity[] = [];

  @Expose()
  @Type(() => ObjectPreferenceEntity)
  objectPreferences?: ObjectPreferenceEntity[]

  @Expose()
  @Type(() => WaterGoPreferenceEntity)
  waterGoPreference?: WaterGoPreferenceEntity

  @Expose()
  @Type(() => OilGoPreferenceEntity)
  oilGoPreferenceEntity?: OilGoPreferenceEntity

  /**
   * Constructor for CompanyRegionProfileEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a CompanyRegionProfileEntity.
   *
   * @param companyId The primary key of the company.
   * @param regionId The primary key of the region.
   */
  static create(
    companyId: string,
    regionId: string
  ): CompanyRegionProfileEntity {
    const entity = new CompanyRegionProfileEntity();
    entity.companyId = companyId;
    entity.regionId = regionId;
    return entity;
  }

  /**
   * Create a CompanyRegionProfileEntity from a CompanyRegionProfile type value.
   *
   * @param companyRegionProfile The company region profile type value.
   */
  static from(
    companyRegionProfile: CompanyRegionProfile
  ): CompanyRegionProfileEntity {
    return plainToClass(CompanyRegionProfileEntity, companyRegionProfile, {
      excludeExtraneousValues: true,
    });
  }
}
